import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useAllClassifiedContactPolicies } from '../../../../queries';
import { CustomDateRange } from '../../CustomDateRange';
import { PolicySelector } from '../../email/PolicySelector';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { AGE_RANGE_KEY, KeyFactErrors } from '../hooks/useKeyFactFilters';
import { styleSheet } from './styles';

interface KeyFactOption {
	value: Api.KeyDateKind | typeof AGE_RANGE_KEY;
	label: string;
}

const MIN_AGE = 15;

interface AgeOption {
	value: number | null;
	label: string;
}

const noMinOption: AgeOption = {
	value: null,
	label: 'No Min Age',
};
const noMaxOption: AgeOption = {
	value: null,
	label: 'No Max Age',
};

const allAges: number[] = Array.from(Array(100 - MIN_AGE).keys());
const ageOptions: AgeOption[] = [...allAges.map((number: number) => number + MIN_AGE)].map(age => {
	return {
		value: age,
		label: age.toString(),
	};
});

const minAgeOptions: AgeOption[] = [noMinOption, ...ageOptions];
const maxAgeOptions: AgeOption[] = [noMaxOption, ...ageOptions];

const keyFactOptions: KeyFactOption[] = [
	{ value: Api.KeyDateKind.Renewal, label: 'Renewals' },
	{ value: Api.KeyDateKind.Anniversary, label: 'Anniversaries' },
	{ value: AGE_RANGE_KEY, label: 'Age Range' },
	{ value: Api.KeyDateKind.Birthday, label: 'Upcoming Birthdays' },
	{ value: Api.KeyDateKind.FinancialReview, label: 'Annual Reviews' },
];

interface KeyFactFiltersDropdownProps {
	selectedKeyFact: Api.KeyDateKind | typeof AGE_RANGE_KEY | null;
	setSelectedKeyFact: (value: Api.KeyDateKind | typeof AGE_RANGE_KEY | null) => void;
	selectedPolicies: string[];
	setSelectedPolicies: (value: string[]) => void;
	selectedKeyFactStartDate: Date;
	setSelectedKeyFactStartDate: (value: Date) => void;
	selectedKeyFactEndDate: Date;
	setSelectedKeyFactEndDate: (value: Date) => void;
	selectedMinAge: number;
	setSelectedMinAge: (value: number) => void;
	selectedMaxAge: number;
	setSelectedMaxAge: (value: number) => void;
	keyFactErrors: string[];
	className?: string;
	styles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
}

export const KeyFactFiltersDropdown: React.FC<KeyFactFiltersDropdownProps> = ({
	selectedKeyFact,
	setSelectedKeyFact,
	selectedPolicies,
	setSelectedPolicies,
	selectedKeyFactStartDate,
	setSelectedKeyFactStartDate,
	selectedKeyFactEndDate,
	setSelectedKeyFactEndDate,
	selectedMinAge,
	setSelectedMinAge,
	selectedMaxAge,
	setSelectedMaxAge,
	keyFactErrors,
	className,
	styles,
	impersonationContext,
}) => {
	const contactsRequest: Api.IResourceSelectorContactsRequest = {
		filter: {},
	};

	const { data: classifiedPolicies, isPending: isLoadingPolicies } = useAllClassifiedContactPolicies({
		contactsRequest,
		enabled: !!selectedKeyFact,
		refetchOnWindowFocus: false,
		impersonationContext,
	});

	const onKeyFactOptionChanged = (value: Api.KeyDateKind | typeof AGE_RANGE_KEY) => {
		setSelectedKeyFact(value);
	};

	const onSelectedPoliciesChanged = (values: string[]) => {
		setSelectedPolicies(values);
	};

	const onSelectedMinAgeChanged = (value: number | null) => {
		setSelectedMinAge(value);
	};

	const onSelectedMaxAgeChanged = (value: number | null) => {
		setSelectedMaxAge(value);
	};

	const ageRangeMessage = React.useMemo(() => {
		if (selectedKeyFact !== AGE_RANGE_KEY || !selectedKeyFactEndDate) {
			return '';
		}

		const estimatedDateOfFilterCreation = moment(selectedKeyFactEndDate).format('MMMM Do');
		const today = moment().format('MMMM Do');

		if (today === estimatedDateOfFilterCreation) {
			return 'as of today';
		}
		return ` as of ${estimatedDateOfFilterCreation} (when filter was created)`;
	}, [selectedKeyFactEndDate, selectedKeyFact]);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<Select
				items={keyFactOptions}
				value={selectedKeyFact ?? undefined}
				onValueChange={value => onKeyFactOptionChanged(value as Api.KeyDateKind | typeof AGE_RANGE_KEY)}
			>
				<SelectTrigger styles={[styleSheet.fullWidth]}>
					<SelectValue placeholder='Select key fact' />
				</SelectTrigger>
				<SelectContent>
					{keyFactOptions.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{selectedKeyFact ? (
				<>
					{selectedKeyFact === Api.KeyDateKind.Renewal ? (
						<>
							<h6 className={css(styleSheet.subtitle)}>Policy</h6>
							<PolicySelector
								classifiedPolicies={classifiedPolicies}
								isLoadingClassifiedPolicies={isLoadingPolicies}
								onSelectedPoliciesChanged={onSelectedPoliciesChanged}
								selectedPolicies={selectedPolicies}
							/>

							{keyFactErrors.indexOf(KeyFactErrors.Policy) > -1 ? (
								<p role='alert' className={css(styleSheet.formErrorLine)}>
									Must select a policy for renewal
								</p>
							) : null}

							<h6 className={css(styleSheet.subtitle)}>Policy Renewal Date Range</h6>
						</>
					) : null}

					{selectedKeyFact === AGE_RANGE_KEY ? (
						<>
							<div className={css(styleSheet.twoColumn)}>
								<div className={css(styleSheet.fullWidth)}>
									<h6 className={css(styleSheet.subtitle)}>Min Age</h6>

									<Select items={minAgeOptions} value={selectedMinAge} onValueChange={onSelectedMinAgeChanged}>
										<SelectTrigger styles={[styleSheet.fullWidth]}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{minAgeOptions.map(option => (
												<SelectItem key={option.label} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className={css(styleSheet.fullWidth)}>
									<h6 className={css(styleSheet.subtitle)}>Max Age</h6>

									<Select items={maxAgeOptions} value={selectedMaxAge} onValueChange={onSelectedMaxAgeChanged}>
										<SelectTrigger styles={[styleSheet.fullWidth]}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{maxAgeOptions.map(option => (
												<SelectItem key={option.label} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
							<span className={css(styleSheet.ageRange)}>{ageRangeMessage}</span>
						</>
					) : null}

					{selectedKeyFact === Api.KeyDateKind.Anniversary ? (
						<h6 className={css(styleSheet.subtitle)}>Anniversary Date Range</h6>
					) : null}

					{selectedKeyFact === Api.KeyDateKind.Birthday ? (
						<h6 className={css(styleSheet.subtitle)}>Birthday Date Range</h6>
					) : null}

					{selectedKeyFact === Api.KeyDateKind.FinancialReview ? (
						<h6 className={css(styleSheet.subtitle)}>Annual Review Date Range</h6>
					) : null}

					{selectedKeyFact !== AGE_RANGE_KEY ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setSelectedKeyFactEndDate(null);
								setSelectedKeyFactStartDate(null);
							}}
							defaultOpen={!selectedKeyFactEndDate}
							from={selectedKeyFactStartDate || null}
							to={selectedKeyFactEndDate || null}
							onChange={(from, to) => {
								setSelectedKeyFactStartDate(from);
								if (to !== from) {
									setSelectedKeyFactEndDate(to);
								}
							}}
							hasStartDateError={keyFactErrors.indexOf(KeyFactErrors.StartDate) > -1}
							hasEndDateError={keyFactErrors.indexOf(KeyFactErrors.EndDate) > -1}
							showTextInput
						/>
					) : null}
				</>
			) : null}
		</div>
	);
};
